import React, {useCallback, useMemo, useState} from 'react';
import {collectDescendantFolderIds} from '../utils/sourcePackageHelpers';
import FolderRowView from './FolderRowView';
import FolderMenu from './FolderMenu';
import DeleteFolderDialog from './DeleteFolderDialog';
import MoveFolderDialog from './MoveFolderDialog';

/**
 * FolderRow - Container component for folder display and management.
 * Handles all folder operations: rename, delete, move, and snippet drops.
 */
const FolderRow = ({
                       folderId,
                       name,
                       count = 0,
                       onOpen,
                       onDropSnippet,
                       onRename,
                       folders = [],
                       renameFolder,
                       removeFolder,
                       moveFolder,
                       moveItem,
                   }) => {
    // UI State
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [draftName, setDraftName] = useState(name || '');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [moveDialogOpen, setMoveDialogOpen] = useState(false);
    const [moveTarget, setMoveTarget] = useState('');

    // Sync draft name when prop changes while not editing
    React.useEffect(() => {
        if (!isEditing) {
            setDraftName((name || '').slice(0, 128));
        }
    }, [name, isEditing]);

    // Build move options excluding folder and its descendants
    const moveOptions = useMemo(() => {
        const invalidTargets = new Set([folderId, ...collectDescendantFolderIds(folders, folderId)]);

        const validFolders = folders
            .filter(f => !invalidTargets.has(f.id))
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .map(f => ({id: f.id, name: f.name || 'Untitled'}));

        return [{id: '', name: '(Root)'}, ...validFolders];
    }, [folders, folderId]);

    // Rename handlers
    const commitRename = useCallback(() => {
        const trimmed = (draftName || '').trim().slice(0, 128);
        if (trimmed && trimmed !== name) {
            renameFolder?.(folderId, trimmed);
            onRename?.(folderId, trimmed);
        }
        setIsEditing(false);
    }, [draftName, name, folderId, renameFolder, onRename]);

    const cancelRename = useCallback(() => {
        setDraftName(name || '');
        setIsEditing(false);
    }, [name]);

    // Menu handlers
    const handleMenuOpen = useCallback((event) => setMenuAnchor(event.currentTarget), []);
    const handleMenuClose = useCallback(() => setMenuAnchor(null), []);

    const handleStartRename = useCallback(() => setIsEditing(true), []);

    const handleOpenDeleteDialog = useCallback(() => setDeleteDialogOpen(true), []);
    const handleCloseDeleteDialog = useCallback(() => setDeleteDialogOpen(false), []);
    const handleConfirmDelete = useCallback(() => {
        removeFolder?.(folderId);
        setDeleteDialogOpen(false);
    }, [folderId, removeFolder]);

    const handleOpenMoveDialog = useCallback(() => {
        const currentParent = folders.find(f => f.id === folderId)?.parentFolderId || '';
        setMoveTarget(currentParent === null ? '' : currentParent);
        setMoveDialogOpen(true);
    }, [folders, folderId]);
    const handleCloseMoveDialog = useCallback(() => setMoveDialogOpen(false), []);
    const handleConfirmMove = useCallback(() => {
        // position is omitted — moveFolder will default to append at end of new parent's siblings
        const parent = moveTarget === '' ? null : moveTarget;
        moveFolder?.(folderId, parent);
        setMoveDialogOpen(false);
    }, [folderId, moveFolder, moveTarget]);

    // Drag handlers for snippet drops
    const handleDragOver = useCallback((e) => e.dataTransfer && e.preventDefault(), []);

    const handleDragEnter = useCallback((e) => {
        e.currentTarget?.classList?.add('drag-over');
        e.dataTransfer && e.preventDefault();
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.currentTarget?.classList?.remove('drag-over');
    }, []);

    const handleDrop = useCallback((e) => {
        e.currentTarget?.classList?.remove('drag-over');
        e.preventDefault();
        e.stopPropagation();

        try {
            const raw = e.dataTransfer.getData('application/x-snippet');
            if (!raw) return;
            const data = JSON.parse(raw);

            // If the dragged item already lives in the source package, move it
            if (data.itemId && moveItem) {
                moveItem(data.itemId, folderId || null);
            } else if (onDropSnippet) {
                onDropSnippet(folderId, data);
            }
        } catch {
            // Ignore invalid drops
        }
    }, [folderId, onDropSnippet, moveItem]);

    return (
        <>
            <FolderRowView
                name={name}
                count={count}
                isEditing={isEditing}
                draftName={draftName}
                onDraftChange={setDraftName}
                onCommitRename={commitRename}
                onCancelRename={cancelRename}
                onMenuOpen={handleMenuOpen}
                onRowClick={() => onOpen?.(folderId)}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            />

            <FolderMenu
                anchorEl={menuAnchor}
                onClose={handleMenuClose}
                onRename={handleStartRename}
                onMove={handleOpenMoveDialog}
                onDelete={handleOpenDeleteDialog}
            />

            <DeleteFolderDialog
                open={deleteDialogOpen}
                folderName={name}
                onClose={handleCloseDeleteDialog}
                onConfirm={handleConfirmDelete}
            />

            <MoveFolderDialog
                open={moveDialogOpen}
                folderId={folderId}
                currentTarget={moveTarget}
                moveOptions={moveOptions}
                onTargetChange={setMoveTarget}
                onClose={handleCloseMoveDialog}
                onConfirm={handleConfirmMove}
            />
        </>
    );
};

export default FolderRow;
